<template>
  <m-dropdown
    :value="open"
    :disabled="readOnly"
    :title="$t('listFilter.title')"
    placement="bottomLeft"
    @update:value="emit('update:open', $event)"
  >
    <slot name="trigger" />
    <template #overlay>
      <m-card
        class="quick-number-scope"
        no-padding
      >
        <m-content :padding-y="4">
          <m-content :padding-x="5">
            <quick-filter-header
              :label="scopeItem.text"
              @delete="emit('delete')"
              @add-to-advanced="emit('addToAdvanced')"
            >
              <template #op>
                <m-select
                  :value="selectedType"
                  :items="intervalTypes"
                  :read-only="readOnly"
                  xs
                  hide-border
                  light
                  :trigger-style="{ textTransform: 'lowercase' }"
                  @input="updateSelectedType"
                />
              </template>
            </quick-filter-header>
            <template
              v-if="!isEmpty"
            >
              <m-input-number
                class="_input"
                :value="number"
                :read-only="readOnly"
                :formatter="formatter"
                :parser="parser"
                nullable
                auto-focus
                @input="updateNumber"
                @keydown.enter="emit('update:open', false)"
              />
            </template>
          </m-content>
        </m-content>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import QuickFilterHeader from '@/components/filter/quick/QuickFilterHeader.vue';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useScopeItem from '@/composables/filter/scope-item';
import { AND } from 'shared/api/query/constants';
import { EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { computed, ref, watch } from 'vue';
import { intervalType, numberFormat, propertyType, userScopeType } from 'shared/constants.json';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { numberFormatter, numberParser } from '@/lib/props/number';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  scopeItem: {
    type: Object,
    required: true,
  },
  node: {
    type: Object,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  hideIsEmpty: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:open', 'update:scope', 'update:not-operator', 'delete', 'addToAdvanced']);

const { operatorTypes: getOperatorTypes } = useScopeItem();
const intervalTypes = computed(() => getOperatorTypes(propertyType.number, { hideEmpty: props.hideIsEmpty }));

const { userLang } = useLoggedInUser();
const format = computed(() => {
  if (props.scopeItem.type === userScopeType.property) {
    return props.scopeItem.property.numberFormat;
  }
  return numberFormat.number;
});
const formatter = computed(() => numberFormatter(format.value, userLang.value));
const parser = computed(() => numberParser(format.value, userLang.value));

const rangeKey = computed(() => {
  if ([intervalType.lt, intervalType.le].includes(selectedType.value)) {
    return 'maxType';
  }
  return 'minType';
});
const numberKey = computed(() => {
  if ([intervalType.lt, intervalType.le].includes(selectedType.value)) {
    return 'max';
  }
  return 'min';
});
const numberRange = computed(() => ({
  [numberKey.value]: number.value,
  [rangeKey.value]: selectedType.value,
}));

const isNot = computed(() => props.node.op === NOT);
const scope = computed(() => props.node.children[0].scope);

const selectedType = ref(intervalType.eq);
const isEmpty = ref(false);
const updateSelectedType = (value) => {
  if (value !== NOT_EMPTY && isNot.value) {
    emit('update:not-operator', AND);
  }
  if (value === NOT_EMPTY && !isNot.value) {
    emit('update:not-operator', NOT);
  }

  if ([EMPTY, NOT_EMPTY].includes(value)) {
    isEmpty.value = true;
    emit('update:scope', { ...scope.value, isEmpty: isEmpty.value });
    return;
  }
  isEmpty.value = false;
  selectedType.value = value;
  emit('update:scope', { ...scope.value, numberRange: numberRange.value, isEmpty: isEmpty.value });
};

const number = ref(null);
const updateNumber = (value) => {
  number.value = value;
  emit('update:scope', { ...scope.value, numberRange: numberRange.value });
};

const setValues = ({ numberRange, isEmpty: isEmptyParam = false }) => {
  isEmpty.value = isEmptyParam;
  if (isEmptyParam === true) {
    selectedType.value = isNot.value ? NOT_EMPTY : EMPTY;
    return;
  }
  if ([intervalType.lt, intervalType.le].includes(numberRange.maxType)) {
    selectedType.value = numberRange.maxType;
    number.value = numberRange.max;
    return;
  }
  selectedType.value = numberRange.minType;
  number.value = numberRange.min;
};
setValues(scope.value);
watch(scope, (value) => {
  if (isNullOrUndefined(value)) {
    return;
  }
  setValues(value);
}, { deep: true });
watch(isNot, () => {
  setValues(scope.value);
});
</script>

<style scoped lang="scss">
.quick-number-scope {
  ._input {
    margin: 0 .4rem .4rem .4rem;
  }
}
</style>
